// src/components/molecules/landing/header-nav.tsx
"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ArrowRight, ChevronDown } from "lucide-react";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import { cn } from "@/lib/utils";
import { isNonRouterHref } from "@/lib/links";

/** The blog article card shown on the left of a mega panel. */
export type NavFeature = {
  href: string;
  title: string;
  excerpt?: string;
  image?: { src: string; alt: string; width: number; height: number };
};

export type NavItem = {
  label: string;
  href: string;
  /** Lucide slug from the menu item's `icon-*` CSS class. */
  icon?: string;
  /** Small label above this item's links in the panel, e.g. "Our products". */
  heading?: string;
  children?: NavItem[];
  feature?: NavFeature;
};

type HeaderNavProps = {
  items: NavItem[];
  className?: string;
};

const ITEM_CLASSES =
  "min-h-11 inline-flex items-center rounded-lg px-4 py-3 font-display text-base font-medium tracking-[0.02em] transition-colors";

const ACTIVE_CLASSES =
  "bg-white text-brand-ink shadow-md shadow-brand-ink/10 ring-1 ring-brand-border/60";

/** Icons are authored in WordPress, so the name is only known at runtime. */
function NavIcon({ name }: { name?: string }) {
  if (!name) return null;
  return (
    <DynamicIcon
      name={name as IconName}
      className="size-5 shrink-0 text-brand"
      aria-hidden
      fallback={() => <span className="size-5 shrink-0" aria-hidden />}
    />
  );
}

/** A link that stays out of the router when the href is external or proxied. */
function NavLink({
  item,
  className,
  active,
  onNavigate,
  children,
}: {
  item: { href: string; label?: string };
  className: string;
  active?: boolean;
  onNavigate?: () => void;
  children?: React.ReactNode;
}) {
  const body = children ?? item.label;
  if (isNonRouterHref(item.href)) {
    return (
      <a href={item.href} className={className} onClick={onNavigate}>
        {body}
      </a>
    );
  }
  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={className}
      onClick={onNavigate}
    >
      {body}
    </Link>
  );
}

function FeatureCard({
  feature,
  onNavigate,
}: {
  feature: NavFeature;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      item={{ href: feature.href }}
      onNavigate={onNavigate}
      className="group w-full shrink-0 overflow-hidden rounded-xl border border-brand-border bg-white transition-shadow hover:shadow-md lg:w-80"
    >
      {feature.image && (
        <Image
          src={feature.image.src}
          alt={feature.image.alt}
          width={feature.image.width}
          height={feature.image.height}
          className="aspect-video w-full object-cover"
          sizes="320px"
        />
      )}
      <span className="block p-4">
        <span className="block text-xs font-semibold uppercase tracking-[0.08em] text-brand">
          From the blog
        </span>
        <span className="mt-2 block font-display text-base font-semibold leading-snug text-brand-ink">
          {feature.title}
        </span>
        {feature.excerpt && (
          <span className="mt-2 line-clamp-3 block text-sm text-brand-ink/70">
            {feature.excerpt}
          </span>
        )}
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand">
          Read article
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </span>
      </span>
    </NavLink>
  );
}

export function HeaderNav({ items, className }: HeaderNavProps) {
  const pathname = usePathname();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelPrefix = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openNow = useCallback(
    (key: string) => {
      cancelClose();
      setOpenKey(key);
    },
    [cancelClose],
  );

  /** Grace period so the pointer can cross the gap from trigger to panel. */
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenKey(null), 140);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Close any open panel on route change — adjusted during render, not in an
  // Effect (react-hooks/set-state-in-effect), per
  // https://react.dev/learn/you-might-not-need-an-effect. prevPathname
  // tracks the last render's pathname so this only fires on the transition.
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setOpenKey(null);
  }

  useEffect(() => {
    if (!openKey) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const key = openKey;
      setOpenKey(null);
      triggerRefs.current[key]?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenKey(null);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [openKey]);

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      className={className}
      onMouseLeave={closeSoon}
    >
      <ul className="flex items-center gap-1">
        {items.map((item) => {
          const key = `${item.label}-${item.href}`;
          const children = item.children ?? [];
          const selfActive = !isNonRouterHref(item.href) && pathname === item.href;

          if (!children.length && !item.feature) {
            return (
              <li key={key}>
                <NavLink
                  item={item}
                  active={selfActive}
                  className={cn(
                    ITEM_CLASSES,
                    selfActive ? ACTIVE_CLASSES : "text-brand-ink hover:bg-white/70",
                  )}
                />
              </li>
            );
          }

          // A parent counts as current when the page is one of its children.
          const active =
            selfActive || children.some((child) => pathname === child.href);
          const open = openKey === key;
          const panelId = `${panelPrefix}-${item.label.replace(/[^a-z0-9]+/gi, "-")}`;

          return (
            <li key={key}>
              <button
                type="button"
                ref={(el) => {
                  triggerRefs.current[key] = el;
                }}
                aria-expanded={open}
                aria-controls={panelId}
                onMouseEnter={() => openNow(key)}
                // Deliberately no open-on-focus: Escape returns focus here, and
                // reopening on that focus makes the panel impossible to dismiss.
                onClick={() => (open ? setOpenKey(null) : openNow(key))}
                className={cn(
                  ITEM_CLASSES,
                  "gap-1",
                  active || open ? ACTIVE_CLASSES : "text-brand-ink hover:bg-white/70",
                )}
              >
                {item.label}
                <ChevronDown
                  className={cn("size-4 transition-transform", open && "rotate-180")}
                  aria-hidden
                />
              </button>

              {open && (
                // Full-bleed: absolute against the sticky <header>, which is the
                // nearest positioned ancestor, so the panel escapes the 1280px
                // container while its contents stay aligned to it.
                <div
                  id={panelId}
                  onMouseEnter={cancelClose}
                  className="absolute inset-x-0 top-full border-t border-brand-border bg-white shadow-lg shadow-brand-ink/5"
                >
                  <div className="mx-auto flex w-full max-w-[1280px] gap-10 px-4 py-8 sm:px-8 lg:px-20">
                    {item.feature && (
                      <FeatureCard
                        feature={item.feature}
                        onNavigate={() => setOpenKey(null)}
                      />
                    )}
                    <div className="flex-1">
                      {item.heading && (
                        <p className="mb-3 border-b border-brand-border pb-3 text-xs font-semibold uppercase tracking-[0.08em] text-brand-ink/60">
                          {item.heading}
                        </p>
                      )}
                      <ul
                        className={cn(
                          "grid content-start gap-1",
                          children.length > 4 ? "sm:grid-cols-2" : "sm:grid-cols-1",
                        )}
                      >
                        {children.map((child) => {
                          const childActive =
                            !isNonRouterHref(child.href) && pathname === child.href;
                          return (
                            <li key={`${child.label}-${child.href}`}>
                              <NavLink
                                item={child}
                                active={childActive}
                                onNavigate={() => setOpenKey(null)}
                                className={cn(
                                  "min-h-11 flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium transition-colors",
                                  childActive
                                    ? "bg-brand-muted text-brand-ink"
                                    : "text-brand-ink hover:bg-brand-muted",
                                )}
                              >
                                <NavIcon name={child.icon} />
                                {child.label}
                              </NavLink>
                            </li>
                          );
                          })}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
